import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import OfferAiSummaryBoundary from './OfferAiSummaryBoundary';
import {
  errorMessage,
  getOfferAiSummary,
  regenerateOfferAiSummary,
} from './offerAiSummaryApi';
import './OfferAiSummaryModal.css';

const POLL_MS = 3000;
const POLL_LIMIT_MS = 4 * 60 * 1000;
const REFRESH_MS = 15000;

// Display label for missing values. ai-service's wire protocol still returns
// "Not found in document" (see offer_summary/models.py); the UI renders the
// shorter "None" instead. normUnknown() maps the wire sentinel to the display
// label at the entry of every formatter so echo-back paths don't leak the
// wire string into the table.
const NOT_FOUND = 'None';
const WIRE_NOT_FOUND = 'Not found in document';
const NOT_APPLICABLE = 'Not applicable';
const normUnknown = (v) => (v === WIRE_NOT_FOUND ? NOT_FOUND : v);

// ──────────────────────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────────────────────

const money = (value) => {
  value = normUnknown(value);
  if (value === NOT_FOUND || value === NOT_APPLICABLE) return value;
  if (value && typeof value === 'object' && typeof value.amount === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: value.currency || 'USD',
      maximumFractionDigits: 0,
    }).format(value.amount);
  }
  return NOT_FOUND;
};

const text = (value) => {
  value = normUnknown(value);
  if (value === null || value === undefined || value === '') return NOT_FOUND;
  if (value === NOT_FOUND || value === NOT_APPLICABLE) return value;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

const date = (value) => text(value);

const buyerAgent = (value) => {
  value = normUnknown(value);
  if (value === NOT_FOUND) return NOT_FOUND;
  if (!value || typeof value !== 'object') return NOT_FOUND;
  const name = value.name || '';
  const brokerage = value.brokerage || '';
  if (!name && !brokerage) return NOT_FOUND;
  if (name && brokerage) return `${name} — ${brokerage}`;
  return name || brokerage;
};

const earnestMoney = (value) => {
  value = normUnknown(value);
  if (value === NOT_FOUND || value === NOT_APPLICABLE) return value;
  if (value && typeof value === 'object') {
    const amt = typeof value.amount === 'number' ? money({ amount: value.amount }) : null;
    const dueBy = value.dueBy ? ` · due ${value.dueBy}` : '';
    return `${amt || NOT_FOUND}${dueBy}`;
  }
  return NOT_FOUND;
};

const loanDownPayment = (value) => {
  value = normUnknown(value);
  if (value === NOT_FOUND || value === NOT_APPLICABLE) return value;
  if (value && typeof value === 'object') {
    const loan = typeof value.loanAmount === 'number' ? money({ amount: value.loanAmount }) : null;
    const down = typeof value.downPayment === 'number' ? money({ amount: value.downPayment }) : null;
    const parts = [];
    if (loan) parts.push(`Loan ${loan}`);
    if (down) parts.push(`Down ${down}`);
    return parts.length ? parts.join(' · ') : NOT_FOUND;
  }
  return NOT_FOUND;
};

const contingencies = (list) => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((c) => {
    const label = c.type === 'OTHER' && c.label ? c.label : (c.type || 'Other');
    const deadline = normUnknown(c.deadline) || NOT_FOUND;
    return `${label}: ${deadline}`;
  });
};

const sellerCredits = (value) => {
  value = normUnknown(value);
  if (value === NOT_FOUND || value === NOT_APPLICABLE) return value;
  if (value && typeof value === 'object' && typeof value.amount === 'number') {
    const amt = money({ amount: value.amount });
    return value.purpose ? `${amt} — ${value.purpose}` : amt;
  }
  return NOT_FOUND;
};

const possession = (value) => {
  value = normUnknown(value);
  if (value === NOT_FOUND) return value;
  if (value && typeof value === 'object') {
    const parts = [];
    if (value.timing) parts.push(value.timing);
    if (value.deliveryCondition) parts.push(value.deliveryCondition);
    return parts.length ? parts.join(' · ') : NOT_FOUND;
  }
  return NOT_FOUND;
};

const escalation = (value) => {
  if (!value || typeof value !== 'object') return 'No escalation';
  if (value.enabled !== true) return 'No escalation';
  const inc = typeof value.increment === 'number' ? money({ amount: value.increment }) : NOT_FOUND;
  const cap = typeof value.cap === 'number' ? money({ amount: value.cap }) : NOT_FOUND;
  const trigger = normUnknown(value.trigger) || NOT_FOUND;
  return `Increment ${inc} · Cap ${cap} · ${trigger}`;
};

const inclusionsExclusions = (value) => {
  const includes = Array.isArray(value?.inclusions) ? value.inclusions : [];
  const excludes = Array.isArray(value?.exclusions) ? value.exclusions : [];
  return { includes, excludes };
};

// ──────────────────────────────────────────────────────────────────────────────
// Rendered rows (field, value) in display order per Phase 0 field list.
// ──────────────────────────────────────────────────────────────────────────────

const FIELD_ROWS = [
  { key: 'buyer', label: 'Buyer', render: (f) => text(f.buyer) },
  { key: 'buyerAgent', label: 'Buyer agent', render: (f) => buyerAgent(f.buyerAgent) },
  { key: 'price', label: 'Price', render: (f) => money(f.price) },
  { key: 'earnestMoney', label: 'Earnest money', render: (f) => earnestMoney(f.earnestMoney) },
  { key: 'financing', label: 'Financing', render: (f) => text(f.financing) },
  { key: 'loanDownPayment', label: 'Loan / down payment', render: (f) => loanDownPayment(f.loanDownPayment) },
  { key: 'closingDate', label: 'Closing date', render: (f) => date(f.closingDate) },
  { key: 'offerExpiration', label: 'Offer expires', render: (f) => date(f.offerExpiration) },
  { key: 'sellerCredits', label: 'Seller credits', render: (f) => sellerCredits(f.sellerCredits) },
  { key: 'possession', label: 'Possession', render: (f) => possession(f.possession) },
  { key: 'escalation', label: 'Escalation', render: (f) => escalation(f.escalation) },
  { key: 'preApproval', label: 'Pre-approval', render: (f) => (f.preApproval === 'Y' ? 'Attached' : 'Not attached') },
  { key: 'specialTerms', label: 'Special terms', render: (f) => text(f.specialTerms) },
];

// ──────────────────────────────────────────────────────────────────────────────
// Diff: given v1 fields + a later version's fields, return the subset of
// FIELD_ROWS whose rendered string differs. "contingencies" and
// "inclusionsExclusions" are handled separately (list diff).
// ──────────────────────────────────────────────────────────────────────────────

const changedRows = (prev, next) => FIELD_ROWS.filter((row) => row.render(prev) !== row.render(next));

const contingenciesChanged = (prev, next) =>
  JSON.stringify(contingencies(prev?.contingencies)) !== JSON.stringify(contingencies(next?.contingencies));

const inclusionsExclusionsChanged = (prev, next) => {
  const a = inclusionsExclusions(prev?.inclusionsExclusions);
  const b = inclusionsExclusions(next?.inclusionsExclusions);
  return JSON.stringify(a) !== JSON.stringify(b);
};

// ──────────────────────────────────────────────────────────────────────────────

const ModalInner = ({ transactionId, summaryId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [slow, setSlow] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const mounted = useRef(true);
  // Pending-run start timer lives in a ref so it survives the re-render that
  // happens on every poll. Previously held in a local inside the polling
  // useEffect, which reset on every data change and meant the 4-min "slow"
  // message literally never fired.
  const pendingStartedAt = useRef(null);

  const load = useCallback(async () => {
    try {
      const next = await getOfferAiSummary(transactionId, summaryId);
      if (!mounted.current) return;
      setData(next);
      setError(null);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorMessage(err, 'Could not load the summary.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [transactionId, summaryId]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  // Poll while PENDING; stop and reload once status flips.
  useEffect(() => {
    if (!data || data.status !== 'PENDING') {
      // Reset the timer when we leave PENDING so the next generation starts fresh.
      pendingStartedAt.current = null;
      setElapsedSec(0);
      return undefined;
    }
    if (pendingStartedAt.current === null) {
      pendingStartedAt.current = Date.now();
    }
    const id = setInterval(() => {
      if (!mounted.current) { clearInterval(id); return; }
      const elapsed = Date.now() - pendingStartedAt.current;
      setElapsedSec(Math.floor(elapsed / 1000));
      if (elapsed > POLL_LIMIT_MS) {
        setSlow(true);
        clearInterval(id);
        return;
      }
      load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [data, load]);

  // Low-frequency refresh so other viewers' audit-log entries / regen state
  // land without a manual reload. Mirrors doc-summary's 15 s cadence.
  useEffect(() => {
    if (!data || data.status === 'PENDING') return undefined;
    const id = setInterval(() => { if (mounted.current) load(); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [data, load]);

  const onRegenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      await regenerateOfferAiSummary(transactionId, summaryId);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not regenerate the summary.'));
    } finally {
      setRegenerating(false);
    }
  };

  const isPending = data?.status === 'PENDING';
  const isFailed = data?.status === 'FAILED';
  // READY but versions still empty = brief race between the status flip and
  // the version write landing in our next poll — fall through to the loading
  // state rather than rendering a blank modal.
  const isReady = data?.status === 'READY' && Array.isArray(data?.versions) && data.versions.length > 0;
  const isReadyButEmpty = data?.status === 'READY' && (!Array.isArray(data?.versions) || data.versions.length === 0);
  const versions = isReady ? [...data.versions].sort((a, b) => a.version - b.version) : [];
  const v1 = versions[0] || null;
  const laterVersions = versions.slice(1);

  const canRegenerate = !isPending && !regenerating && (isFailed || isReady);

  return createPortal(
    <div className="oas-backdrop" onClick={onClose}>
      <div className="oas-modal" onClick={(e) => e.stopPropagation()}>
        <header className="oas-header">
          <h2 className="oas-title">Offer summary</h2>
          <button className="oas-close" aria-label="Close" onClick={onClose}>×</button>
        </header>

        {loading && (
          <div className="oas-loading">
            <span className="oas-spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        )}

        {!loading && error && (
          <div className="oas-error">
            {error}
            <button className="oas-btn-inline" onClick={load}>Try again</button>
          </div>
        )}

        {!loading && !error && (isPending || isReadyButEmpty) && (
          <div className="oas-loading">
            <span className="oas-spinner" aria-hidden="true" />
            <div className="oas-loading-text">
              <div>
                {slow
                  ? 'This is taking longer than usual. You can close this window and check back later.'
                  : 'Reading the offer and generating your summary…'}
              </div>
              {!slow && isPending && (
                <div className="oas-loading-sub">
                  Usually under a minute · {elapsedSec}s elapsed
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && isFailed && (
          <div className="oas-error">
            {data.failureReason || 'Could not generate a summary from this offer. Please try again.'}
            {canRegenerate && (
              <button
                className="oas-btn oas-btn-view"
                disabled={regenerating}
                onClick={onRegenerate}
              >
                {regenerating ? 'Regenerating…' : 'Try again'}
              </button>
            )}
          </div>
        )}

        {!loading && !error && isReady && v1 && (
          <>
            {canRegenerate && (
              <div className="oas-actions">
                <button
                  className="oas-btn oas-btn-secondary"
                  disabled={regenerating}
                  onClick={onRegenerate}
                >
                  {regenerating ? 'Regenerating…' : 'Regenerate'}
                </button>
              </div>
            )}

            <div className="oas-disclaimer">
              {v1.disclaimerText || data.disclaimerText
                || 'Generated summary based only on this document. It may be incomplete or inaccurate — refer to the full document for complete details.'}
            </div>

            <section className="oas-section">
              <h3 className="oas-section-title">
                Original offer{v1.generatedAt ? ` · ${v1.generatedAt.slice(0, 10)}` : ''}
              </h3>
              <FieldsTable fields={v1.fields || {}} />
            </section>

            {laterVersions.map((v, idx) => {
              const prev = versions[idx]; // v1 for first addendum, v2 for next, etc.
              return <AddendumSection key={v.version} prev={prev.fields || {}} next={v.fields || {}} version={v} />;
            })}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

// ──────────────────────────────────────────────────────────────────────────────

const FieldsTable = ({ fields }) => {
  const { includes, excludes } = inclusionsExclusions(fields.inclusionsExclusions);
  const list = contingencies(fields.contingencies);
  return (
    <table className="oas-fields">
      <tbody>
        {FIELD_ROWS.map((row) => (
          <tr key={row.key}>
            <th>{row.label}</th>
            <td>{row.render(fields)}</td>
          </tr>
        ))}
        <tr>
          <th>Contingencies</th>
          <td>
            {list.length === 0 ? NOT_FOUND : (
              <ul className="oas-list">{list.map((c, i) => <li key={i}>{c}</li>)}</ul>
            )}
          </td>
        </tr>
        <tr>
          <th>Inclusions / exclusions</th>
          <td>
            {includes.length === 0 && excludes.length === 0 ? NOT_FOUND : (
              <div className="oas-ie">
                {includes.length > 0 && <div><strong>Includes:</strong> {includes.join(', ')}</div>}
                {excludes.length > 0 && <div><strong>Excludes:</strong> {excludes.join(', ')}</div>}
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

// Addendum = "Updates from addendum (date)" + only-changed fields.
const AddendumSection = ({ prev, next, version }) => {
  const diffRows = changedRows(prev, next);
  const contDiff = contingenciesChanged(prev, next);
  const incDiff = inclusionsExclusionsChanged(prev, next);
  if (diffRows.length === 0 && !contDiff && !incDiff) return null;

  const list = contingencies(next?.contingencies);
  const { includes, excludes } = inclusionsExclusions(next?.inclusionsExclusions);

  return (
    <section className="oas-section oas-addendum">
      <h3 className="oas-section-title">
        Updates from addendum{version.generatedAt ? ` · ${version.generatedAt.slice(0, 10)}` : ''}
      </h3>
      <table className="oas-fields">
        <tbody>
          {diffRows.map((row) => (
            <tr key={row.key}>
              <th>{row.label}</th>
              <td>{row.render(next)}</td>
            </tr>
          ))}
          {contDiff && (
            <tr>
              <th>Contingencies</th>
              <td>
                {list.length === 0 ? NOT_FOUND : (
                  <ul className="oas-list">{list.map((c, i) => <li key={i}>{c}</li>)}</ul>
                )}
              </td>
            </tr>
          )}
          {incDiff && (
            <tr>
              <th>Inclusions / exclusions</th>
              <td>
                {includes.length === 0 && excludes.length === 0 ? NOT_FOUND : (
                  <div className="oas-ie">
                    {includes.length > 0 && <div><strong>Includes:</strong> {includes.join(', ')}</div>}
                    {excludes.length > 0 && <div><strong>Excludes:</strong> {excludes.join(', ')}</div>}
                  </div>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
};

const OfferAiSummaryModal = (props) => (
  <OfferAiSummaryBoundary>
    <ModalInner {...props} />
  </OfferAiSummaryBoundary>
);

export default OfferAiSummaryModal;
